package com.wintermar.inspection.feature.data.repository.answer

import com.wintermar.inspection.AppDatabase
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.ui.component.image.util.FileUploadStatus
import com.wintermar.inspection.feature.data.local.db.SqliteCompat
import com.wintermar.inspection.feature.data.remote.mapper.answer.toAnswerFileModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerFileModel
import com.wintermar.inspection.feature.domain.repository.answer.AnswerFileRepository
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

class AnswerFileRepositoryImpl(
    private val appDatabase: AppDatabase
) : AnswerFileRepository {
    override suspend fun insertAnswerFiles(answerFiles: List<AnswerFileModel>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    val executableQuery = SqliteCompat.selectInsertImplementation(
                        appDatabase = appDatabase,
                        upsert = appDatabase.answerFileTableQueries::insertAnswerFile,
                        replace = appDatabase.answerFileTableQueries::insertReplaceAnswerFile
                    )

                    answerFiles.forEach { answerFile ->
                        executableQuery(
                            answerFile.id,
                            answerFile.answerId,
                            answerFile.name,
                            answerFile.originalName,
                            answerFile.path,
                            answerFile.size,
                            answerFile.type,
                            answerFile.blob,
                            answerFile.uploadStatus.name,
                            answerFile.hasGenerateChunk
                        )
                    }
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteAnswerFileById(id: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerFileTableQueries.deleteAnswerFileById(id)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteAnswerFileByAnswerId(answerId: String): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerFileTableQueries.deleteAnswerFilesByAnswerId(answerId)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun deleteAnswerFileByAnswerIds(answerIds: List<String>): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerFileTableQueries.deleteAnswerFilesByAnswerIds(answerIds)
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun getAnswerFiles(answerId: String): Result<List<AnswerFileModel>> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerFileTableQueries
                        .getAnswerFilesByAnswerId(answerId)
                        .executeAsList()
                        .map { it.toAnswerFileModel() }
                }
                Result.Success(result)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun updateUploadStatus(
        fileId: String,
        uploadStatus: FileUploadStatus
    ): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerFileTableQueries.updateUploadStatusById(
                        uploadStatus.name,
                        fileId
                    )
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun updateHasGenerateChunk(
        fileId: String,
        hasGenerateChunk: Boolean
    ): Result<Unit> {
        return withContext(Dispatchers.Default) {
            try {
                appDatabase.transaction {
                    appDatabase.answerFileTableQueries.updateHasGenerateChunkById(
                        hasGenerateChunk,
                        fileId
                    )
                }
                Result.Success(Unit)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }

    override suspend fun checkGeneratedChunk(fileId: String): Result<Boolean> {
        return withContext(Dispatchers.Default) {
            try {
                val result = appDatabase.transactionWithResult {
                    appDatabase.answerFileTableQueries.checkGenerateChunk(fileId).executeAsOne()
                }
                Result.Success(result.hasGenerateChunk)
            } catch (e: Exception) {
                Result.Error(e)
            }
        }
    }
}
